<template>

<div id="app">

	<div class="app-body">
		<router-view :key="$route.fullPath" />
	</div>

	<!-- the one sign-in dialog, hosted at the root so any screen's Topbar can
	     open it via the bus; guests can dismiss and keep playing -->
	<auth-sign-in v-if="signinOpen" v-on:close="signinOpen = false" />

	<!-- the changelog modal, also hosted once at the root (any Topbar's version
	     chip opens it via the bus) -->
	<app-changelog v-if="changelogOpen" v-on:close="changelogOpen = false" />

	<!-- the result-sync prompt, opened by the `sync-open` bus event. The sign-in
	     path passes a pre-computed `plays` gap; a manual open passes `force` and
	     the dialog computes the gap itself (showing a loading state). -->
	<sync-prompt v-if="syncOpen" :plays="syncPlays" :force="syncForce" v-on:close="syncOpen = false" />

	<!-- a single transient error toast: any code can raise it with
	     bus.$emit('error', message); it auto-hides and is dismissable -->
	<div v-if="errorMessage" class="app-toast" role="alert" v-on:click="dismissError">
		<span class="app-toast-text">{{ errorMessage }}</span>
		<button type="button" class="app-toast-close" aria-label="Dismiss" v-on:click.stop="dismissError">&times;</button>
	</div>

</div>

</template>

<script>

import bus from './service/bus'
import SignIn from './components/auth/SignIn.vue'
import Changelog from './components/common/Changelog.vue'
import SyncPrompt from './components/sync/SyncPrompt.vue'

export default {

	name: 'App',

	components: {
		'auth-sign-in': SignIn,
		'app-changelog': Changelog,
		'sync-prompt': SyncPrompt
	},

	data: function() {

		return {
			// whether the sign-in dialog is open (opened from any Topbar via bus)
			signinOpen: false,
			// whether the changelog modal is open (opened from any Topbar via bus)
			changelogOpen: false,
			// the result-sync prompt: open flag, an optional pre-computed gap
			// (sign-in path), and whether a manual open should force-recompute
			syncOpen: false,
			syncPlays: null,
			syncForce: false,
			// the current transient error toast message (empty = hidden)
			errorMessage: '',
			// handle for the auto-hide timer
			errorTimer: null
		}

	},

	created: function() {

		var self = this

		this.openSignin = function() { self.signinOpen = true }
		this.openChangelog = function() { self.changelogOpen = true }
		this.onError = function(message) { self.showError(message) }
		this.onSyncOpen = function(opts) {

			opts = opts || {}
			self.syncPlays = opts.plays || null
			self.syncForce = !!opts.force
			self.syncOpen = true

		}

		bus.$on('signin', this.openSignin)
		bus.$on('changelog', this.openChangelog)
		bus.$on('error', this.onError)
		bus.$on('sync-open', this.onSyncOpen)

		// recognise the stored session once on boot so a signed-in player isn't
		// shown as logged out (Topbar / Stats / admin curate all read this); we
		// don't block render on it — the UI updates when it resolves
		this.$store.dispatch('account/recognise')

	},

	beforeDestroy: function() {

		bus.$off('signin', this.openSignin)
		bus.$off('changelog', this.openChangelog)
		bus.$off('error', this.onError)
		bus.$off('sync-open', this.onSyncOpen)

		if (this.errorTimer) clearTimeout(this.errorTimer)

	},

	methods: {

		showError: function(message) {

			this.errorMessage = message || 'Something went wrong.'

			if (this.errorTimer) clearTimeout(this.errorTimer)

			var self = this

			this.errorTimer = setTimeout(function() { self.errorMessage = '' }, 4000)

		},

		dismissError: function() {

			this.errorMessage = ''

			if (this.errorTimer) {

				clearTimeout(this.errorTimer)
				this.errorTimer = null

			}

		}

	}

}

</script>

<style>

#app {
	min-height: 100vh; /* fallback */
	min-height: calc(var(--vh, 1vh) * 100);
	background: var(--paper);
	color: var(--ink);
	font-family: var(--font-body);
	display: flex;
	flex-direction: column;
}

/*
	The chrome lives outside the router so only the content between it ever
	transitions. Screens fill the body.
*/
.app-body {
	flex: 1;
	position: relative;
	display: flex;
	flex-direction: column;
}

.app-body > * {
	flex: 1;
}

/*
	The global error toast: a small paper card pinned bottom-centre with a
	--wrong accent border. Raised via bus.$emit('error', message), auto-hides.
*/
.app-toast {
	position: fixed;
	left: 50%;
	bottom: 22px;
	transform: translateX(-50%);
	z-index: 200;
	max-width: calc(100vw - 36px);
	display: flex;
	align-items: center;
	gap: 12px;
	background: var(--paper);
	color: var(--ink);
	border: 1px solid var(--wrong);
	border-left-width: 3px;
	border-radius: 10px;
	padding: 12px 14px;
	box-shadow: 0 16px 44px rgba(40, 34, 24, 0.2);
	font-family: var(--font-body);
	font-size: 14px;
	cursor: pointer;
	animation: app-toast-in 0.25s ease both;
}

@keyframes app-toast-in {
	from { transform: translate(-50%, 12px); opacity: 0; }
	to { transform: translate(-50%, 0); opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
	.app-toast { animation: none; }
}

.app-toast-text {
	line-height: 1.4;
}

.app-toast-close {
	flex: none;
	background: none;
	border: none;
	color: var(--muted-2);
	font-size: 18px;
	line-height: 1;
	cursor: pointer;
	padding: 0 2px;
}

/*
	v-tooltip theme (it renders to <body>, so this must be global/unscoped): a
	small ink bubble in mono, matching the editorial palette.
*/
.tooltip {
	display: block !important;
	z-index: 400;
}

.tooltip .tooltip-inner {
	background: var(--ink);
	color: var(--paper);
	font-family: var(--font-mono);
	font-size: 11px;
	letter-spacing: 0.02em;
	padding: 5px 8px;
	border-radius: 4px;
	box-shadow: 0 6px 18px rgba(40, 34, 24, 0.22);
}

.tooltip .tooltip-arrow {
	width: 0;
	height: 0;
	border-style: solid;
	position: absolute;
	margin: 5px;
	border-color: var(--ink);
	z-index: 1;
}

.tooltip[x-placement^="top"] {
	margin-bottom: 6px;
}

.tooltip[x-placement^="top"] .tooltip-arrow {
	border-width: 5px 5px 0 5px;
	border-left-color: transparent;
	border-right-color: transparent;
	border-bottom-color: transparent;
	bottom: -5px;
	left: calc(50% - 5px);
	margin-top: 0;
	margin-bottom: 0;
}

.tooltip[x-placement^="bottom"] {
	margin-top: 6px;
}

.tooltip[x-placement^="bottom"] .tooltip-arrow {
	border-width: 0 5px 5px 5px;
	border-left-color: transparent;
	border-right-color: transparent;
	border-top-color: transparent;
	top: -5px;
	left: calc(50% - 5px);
	margin-top: 0;
	margin-bottom: 0;
}

.tooltip[aria-hidden="true"] {
	visibility: hidden;
	opacity: 0;
	transition: opacity 0.12s, visibility 0.12s;
}

.tooltip[aria-hidden="false"] {
	visibility: visible;
	opacity: 1;
	transition: opacity 0.12s;
}

</style>
